import { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import {
  SESSION_TF, SessionBandPrimitive, buildSessionBands,
  baseChartOptions, TfButtons, SessionLegend,
} from '../utils/chartUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Cap tier config ───────────────────────────────────────────────────────────
// Colors match the stacked-bar widget on SP500Market.
const TIERS = [
  { key: 'mega',  label: 'Mega Cap',  color: '#8b5cf6' },
  { key: 'large', label: 'Large Cap', color: '#3b82f6' },
  { key: 'mid',   label: 'Mid Cap',   color: '#06b6d4' },
  { key: 'small', label: 'Small Cap', color: '#f59e0b' },
  { key: 'micro', label: 'Micro Cap', color: '#6b7280' },
];

// Map TW percentage to Lightweight Charts lineWidth (1–4).
function twToLineWidth(tw) {
  if (tw >= 30) return 4;
  if (tw >= 18) return 3;
  if (tw >= 12) return 2;
  return 1;
}

// Convert CapPoints array to per-tier {time, value} arrays for LineSeries.
function splitTiers(points) {
  const out = {};
  for (const t of TIERS) out[t.key] = [];
  for (const p of points) {
    for (const t of TIERS) {
      if (p[t.key] !== undefined) {
        out[t.key].push({ time: p.time, value: p[t.key] });
      }
    }
  }
  return out;
}

export default function CapSeriesChart({ tf, setTf }) {
  const [tierWeights, setTierWeights] = useState(null); // {mega: 42.2, ...}

  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRefs   = useRef({});   // key → LineSeries
  const primitiveRef = useRef(null);
  const pollRef      = useRef(null);
  const mountedRef   = useRef(true);
  const initialDone  = useRef(false);

  // Create chart once on mount
  useEffect(() => {
    mountedRef.current = true;

    const opts = baseChartOptions(containerRef.current.clientWidth);
    opts.height = 300; // shorter than the candlestick chart
    const chart = createChart(containerRef.current, opts);
    chartRef.current = chart;

    // Add LineSeries for each tier (widths set later when tierWeights arrive).
    const refs = {};
    for (const t of TIERS) {
      const s = chart.addSeries(LineSeries, {
        color:     t.color,
        lineWidth: 2, // placeholder — updated on first data fetch
        priceLineVisible: false,
        lastValueVisible: true,
        crosshairMarkerVisible: true,
        priceFormat: {
          type: 'custom',
          minMove: 0.01,
          formatter: v => (v >= 0 ? '+' : '') + v.toFixed(2) + '%',
        },
      });
      refs[t.key] = s;
    }
    seriesRefs.current = refs;

    // Price scale: centre on zero with a baseline
    chart.priceScale('right').applyOptions({ mode: 0 });

    // Attach session band primitive to any one series (affects whole pane)
    const primitive = new SessionBandPrimitive();
    primitiveRef.current = primitive;
    refs['mega'].attachPrimitive(primitive);

    const handleResize = () => {
      if (containerRef.current && mountedRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('resize', handleResize);
      clearInterval(pollRef.current);
      chart.remove();
      chartRef.current = null;
      seriesRefs.current = {};
      primitiveRef.current = null;
    };
  }, []);

  // Refetch when timeframe changes
  useEffect(() => {
    initialDone.current = false;
    clearInterval(pollRef.current);

    const fetchSeries = async (isInitial) => {
      if (!mountedRef.current || !chartRef.current) return;
      try {
        const token = await getValidToken();
        if (!token || !mountedRef.current) return;

        const res = await fetch(`${API_URL}/api/chart/cap-series?tf=${tf}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mountedRef.current) return;

        const data = await res.json();
        const points = data.series || [];
        const tw     = data.tierWeights || {};

        if (!mountedRef.current) return;

        // Update tier weights state (used for legend TW display)
        setTierWeights(tw);

        // Apply line widths from TW
        for (const t of TIERS) {
          const s = seriesRefs.current[t.key];
          if (!s) continue;
          s.applyOptions({ lineWidth: twToLineWidth(tw[t.key] || 0) });
        }

        if (!points.length) return;

        const split = splitTiers(points);

        if (isInitial || !initialDone.current) {
          for (const t of TIERS) {
            seriesRefs.current[t.key]?.setData(split[t.key]);
          }
          chartRef.current.timeScale().scrollToRealTime();
          initialDone.current = true;
        } else {
          // Update only the last point for each tier
          for (const t of TIERS) {
            const pts = split[t.key];
            if (pts.length) seriesRefs.current[t.key]?.update(pts[pts.length - 1]);
          }
        }

        if (SESSION_TF.has(tf)) {
          primitiveRef.current?.setBands(buildSessionBands(points));
        } else {
          primitiveRef.current?.clearBands();
        }
      } catch { /* ignore — show stale data */ }
    };

    fetchSeries(true);
    // Update every 5 seconds (all timeframes — TI changes with each new bar)
    pollRef.current = setInterval(() => fetchSeries(false), 5000);
    return () => clearInterval(pollRef.current);
  }, [tf]);

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          Cap Series
        </span>

        {/* Timeframe buttons — shared with SPY chart via parent state */}
        <TfButtons tf={tf} setTf={setTf} />

        {SESSION_TF.has(tf) && <SessionLegend />}
      </div>

      {/* Tier legend with live TW */}
      <div className="flex items-center gap-4 flex-wrap mb-3">
        {TIERS.map(t => (
          <span key={t.key} className="flex items-center gap-1.5 text-xs text-gray-400">
            <span className="inline-block w-4 h-0.5 rounded"
              style={{ backgroundColor: t.color, height: `${twToLineWidth(tierWeights?.[t.key] || 0) + 1}px` }} />
            {t.label}
            {tierWeights?.[t.key] != null && (
              <span className="text-gray-500">({tierWeights[t.key].toFixed(1)}%)</span>
            )}
          </span>
        ))}
      </div>

      {/* Empty state */}
      {initialDone.current === false && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-xs pointer-events-none">
          Waiting for first pre-market bars…
        </div>
      )}

      <div ref={containerRef} className="relative" />
    </div>
  );
}
